<template>
    <div class="space-y-8">
      <!-- Loading State -->
      <div v-if="loading" class="fixed inset-0 bg-white bg-opacity-75 flex items-center justify-center z-50">
        <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>

      <section class="bg-white shadow rounded-lg p-6">
        <h3 class="text-lg font-medium text-gray-900 mb-4">Plans</h3>
  
        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <label
            v-for="plan in plans"
            :key="plan.id"
            class="relative flex flex-col h-full p-6 rounded-lg border transition-all duration-200 shadow-md cursor-pointer"
            :class="{
              'border-blue-500 bg-blue-50 ring-2 ring-blue-200': hasSubscription(plan),
              'border-gray-200 bg-white hover:border-blue-400 hover:shadow-lg': !hasSubscription(plan)
            }"
            role="radio"
            :aria-checked="hasSubscription(plan)"
            tabindex="0"
          >
            <input type="radio" class="opacity-0 absolute top-4 right-4" :checked="hasSubscription(plan)" />
  
            <div v-if="plan.product?.images?.length" class="w-full flex justify-center mb-4">
              <img :src="plan.product.images[0]" :alt="(plan.product.name || 'Plan') + ' image'" class="h-16 object-contain" />
            </div>
  
            <h4 class="text-lg font-medium text-gray-900">{{ plan.product?.name || plan.name || 'Plan' }}</h4>
            <p class="text-sm text-gray-500 mt-1">{{ plan.product?.description || plan.description || '' }}</p>
            
            <div class="mt-4 mb-6">
              <span class="text-2xl font-bold text-gray-900">
                {{ formatCurrencyCents(plan.price, plan.currency) }}
              </span>
              <span class="text-sm text-gray-500">/{{ plan.interval }}</span>
            </div>
  
            <ul v-if="Array.isArray(plan.product?.marketing_features) && plan.product.marketing_features.length" class="mt-4 space-y-2 text-sm text-gray-600">
              <li
                v-for="feature in plan.product.marketing_features"
                :key="getFeatureKey(feature)"
                class="flex items-start"
              >
                <svg class="h-5 w-5 text-green-500 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
                </svg>
                {{ getFeatureName(feature) }}
              </li>
            </ul>
            <div class="mt-4 w-full">
              <span v-if="plan.has_subscription === true && plan.subscription_status === 'active'" class="w-full inline-flex justify-center text-gray-500 bg-gray-50 cursor-default items-center px-4 py-2 border border-green-600 text-sm font-bold rounded-md shadow-sm">Current Subscription</span>
              <span v-else-if="plan.has_subscription === true && plan.subscription_status === 'canceled'" type="button" class="w-full inline-flex justify-center text-gray-500 bg-gray-50 cursor-default items-center px-4 py-2 border border-green-600 text-sm font-bold rounded-md shadow-sm">Canceled</span>
              <span v-else-if="plan.has_subscription === true && plan.subscription_status === 'past_due'" type="button" class="w-full inline-flex justify-center text-gray-500 bg-gray-50 cursor-default items-center px-4 py-2 border border-green-600 text-sm font-bold rounded-md shadow-sm">Past Due</span>
              <span v-else-if="plan.has_subscription === true && plan.subscription_status === 'trialing'" type="button" class="w-full inline-flex justify-center text-gray-500 bg-gray-50 cursor-default items-center px-4 py-2 border border-green-600 text-sm font-bold rounded-md shadow-sm">Trialing</span>
              <span v-else-if="plan.has_subscription === true && plan.subscription_status === 'pending'" type="button" class="w-full inline-flex justify-center text-gray-500 bg-gray-50 cursor-default items-center px-4 py-2 border border-green-600 text-sm font-bold rounded-md shadow-sm">Pending Action</span>
                <div v-if="plan.has_subscription === true" class="space-y-2">
                  <template v-if="!plan.cancel_at_period_end">
                    <button 
                  type="button"
                    v-if="showCancelSubscriptionButton"
                    @click.stop.prevent="confirmCancelSubscription(plan, $event)"
                    class="w-full inline-flex mt-2 justify-center bg-red-600 hover:bg-red-700 text-white items-center px-4 py-2 border border-red-300 text-sm font-medium rounded-md shadow-sm transition-colors duration-200"
                    :disabled="cancellingSubscription"
                  >
                    <span v-if="cancellingSubscription === plan.id">
                      Cancelling...
                    </span>
                    <span v-else>Cancel Subscription</span>
                  </button>
                    <button 
                      type="button"
                      v-if="showContactUsButton"
                      @click.stop.prevent="showContactUs()"
                      class="w-full inline-flex mt-2 justify-center bg-blue-600 hover:bg-blue-700 text-white items-center px-4 py-2 border border-blue-300 text-sm font-medium rounded-md shadow-sm transition-colors duration-200"
                    >
                      Contact Us
                    </button>
                  </template>
                  <div v-else class="text-center text-sm text-red-600 font-medium mt-1">
                    Subscription will end on {{ formatDate(plan.period_end) }}
                  </div>
                </div>
              <button v-else-if="plan.is_bundle === true"
                type="button"
                class="w-full inline-flex justify-center bg-blue-600 hover:bg-blue-700 items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white"
                @click.stop.prevent="selectPlan(plan)"
              >
              Select Plan
              </button>
              <button v-else
                type="button"
                class="w-full inline-flex justify-center bg-gray-300 text-gray-700 cursor-not-allowed items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm"
              >Not Available</button>
            </div>
          </label>
        </div>
      </section>
  
  
      <!-- ===== Add-ons (single sub per type; adjust quantity) ===== -->
      <section class="bg-white shadow rounded-lg p-6">
        <h3 class="text-lg font-medium text-gray-900 mb-4">Add-ons</h3>
  
        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <div
            v-for="addon in addons"
            :key="addon.id"
            class="p-4 border rounded-lg transition-all duration-200"
            :class="{
              'border-green-500 ring-1 ring-green-200 shadow-lg': hasSubscription(addon),
              'border-gray-300': !hasSubscription(addon)
            }"
          >
            <div class="flex justify-between items-start">
                <div v-if="addon.product?.images?.length" class="flex justify-center mb-4">
              <img :src="addon.product.images[0]" :alt="(addon.product.name || 'Addon') + ' image'" class="h-16 object-contain" />
            </div>
              <div> 
                <h4 class="font-medium text-gray-900">{{ addon.product?.name || 'Addon' }}</h4>
                <p class="text-sm text-gray-500">{{ addon.product?.description }}</p>
               
                 <!-- Current Subscription -->
              <div v-if="addon.has_subscription && addon.subscription_status === 'active'" class="text-sm text-gray-600">
                <span v-if="addon.has_subscription" class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 mt-1">
                 Active
                </span>
                <span v-if="addon.quantity > 0" class="ml-2 text-gray-400">(Current: {{ addon.quantity }})</span>
              </div>
              </div>
              <p class="text-lg font-semibold text-gray-900">
                {{ formatCurrencyCents(addon.price, addon.currency) }}
                <span class="text-sm font-normal text-gray-500">/{{ addon.interval }}</span>
              </p>
            </div>
  
            <div class="flex items-center space-x-4">
              <div class="flex items-center space-x-3">
              
                <input 
                  type="number" 
                  :min="getBlockSize(addon)" 
                  :step="getBlockSize(addon)"
                  v-model.number="addonQuantities[addon.id]" 
                  @input="handleQuantityInput($event, addon)"
                  class="w-20 border rounded px-2 py-1 text-sm text-center"
                  :placeholder="getBlockSize(addon)"
                  :class="{ 'opacity-50': loadingAddons[addon.id] }"
                  :disabled="loadingAddons[addon.id]"
                  title="Enter quantity in multiples of block size"
                />
                <span class="text-xs text-gray-600">x {{ formatCurrencyCents(addon.price, addon.currency) }}</span>
                <span class="text-xs text-blue-700 font-semibold">= {{ formatCurrencyCents(addonQuantities[addon.id] * addon.price, addon.currency) }}</span>
                <span v-if="addon.interval" class="text-xs text-gray-400 ml-1">/ {{ addon.interval }}</span>
              </div>
              
             
            </div>
  
            <!-- Update button to confirm quantity changes -->
            <div class="mt-3">
              <button 
                type="button" 
                @click="updateAddonQuantity(addon.id, getAddonQuantity(addon.id))" 
                :disabled="loadingAddons[addon.id]"
                :class="{
                  'bg-blue-600 hover:bg-blue-700': !addon.has_subscription,
                  'bg-green-600 hover:bg-green-700': addon.has_subscription,
                  'opacity-50 cursor-not-allowed': loadingAddons[addon.id]
                }"
                class="w-full inline-flex justify-center items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white"
              >
                <span v-if="loadingAddons[addon.id]">Updating...</span>
                <span v-else><span class="font-medium" v-if="addon.has_subscription">Add More</span><span v-else>Enable Add-on</span></span>
              </button>
            </div>
          </div>
        </div>
      </section>
      
      <!-- Preview Modal -->
      <SubscriptionPreview 
        v-if="showPreview && previewData"
        v-model="showPreview"
        :preview-data="previewData"
        :is-committing="isUpdating"
        :current-plan="currentPlan"
        :new-plan="selectedPlan"
        @confirm="handleCommit"
        @close="showPreview = false"
      />
    </div>
</template>

<script>
import { mapState, mapActions } from 'vuex';
import { formatCurrencyCents } from '../../utils/formatters';
import SubscriptionPreview from './SubscriptionPreview.vue';
import axios from 'axios';

export default {
  name: 'SubscriptionManagement',
  components: {
    SubscriptionPreview
  },
  data() {
    return {
      selectedPlan: null,
      showPreview: false,
      error: null,
      loadingAddons: {},
      isUpdating: false,
      updateError: null,
      updateSuccess: false,
      addonQuantities: {},
      currentPlan: null,
      previewData: null,
      localPreview: null,
      cancellingSubscription: null,
      subscriptionToCancel: null,
      showCancelConfirm: false,
      showCancelSubscriptionButton: false,
      showContactUsButton: true
    };
  },
  computed: {
    ...mapState('billing', ['catalog', 'selection']),
    loading: {
      get() {
        return this.$store.state.billing.loading;
      },
      set(value) {
        this.$store.commit('billing/SET_LOADING', value);
      }
    },
    currentSubscription() {
      return (this.previewData || {}).current_subscription || {};
    },
    plans() {
      if (!this.catalog || !Array.isArray(this.catalog)) return [];
      return this.catalog.filter(plan => 
        plan.active !== false && 
        plan.price && 
        plan.interval &&
        plan.product_metadata?.category === 'plan'
      );
    },
    addons() {
      if (!this.catalog || !Array.isArray(this.catalog)) return [];
      return this.catalog.filter(addon => 
        addon.active !== false && 
        addon.price && 
        addon.interval &&
        addon.product_metadata?.category === 'addon'
      );
    }
  },
  async created() {
    try {
      this.loading = true;
      await this.fetchCatalog();
      
      // Initialize addon quantities after catalog is loaded
      await this.$nextTick(); // Wait for the next tick to ensure addons are populated
      this.addons.forEach(addon => {
        const currentQty = addon.block_size;
        this.$set(this.addonQuantities, addon.id, currentQty);
      });
      
    } catch (error) {
      console.error('Error loading subscription data:', error);
      this.error = 'Failed to load subscription information. Please try again later.';
      this.$emit('error', this.error);
    } finally {
      this.loading = false;
    }
  },
  methods: {
    showContactUs() {
      this.$swal({
        title: 'Contact Support',
        html: `
          <div class="text-left">
            <p class="mb-4">Please contact our support team for any subscription-related inquiries or assistance.</p>
            <div class="bg-gray-50 p-4 rounded-lg">
              <p class="font-medium">Support Email</p>
              <p class="text-blue-600">contact@REoptimizer.com</p>
              <p class="mt-2 font-medium">Phone</p>
              <p>+1 (800) 123-4567</p>
            </div>
          </div>
        `,
        confirmButtonText: 'Close',
        confirmButtonColor: '#3b82f6',
        showCloseButton: true
      });
    },
    formatDate(timestamp) {
      return new Date(timestamp).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    },
    
    async cancelSubscription(plan) {
      this.cancellingSubscription = plan.id;
      
      try {
        await axios.post(`/api/billing/subscriptions/${plan.stripe_subscription_id}/cancel`);
        
        // Update the plan in the catalog to reflect the cancellation
        const planIndex = this.plans.findIndex(p => p.id === plan.id);
        if (planIndex !== -1) {
          this.plans[planIndex] = {
            ...this.plans[planIndex],
            cancel_at_period_end: true,
          };
          
          // Parse the date string from the database (format: 'YYYY-MM-DD HH:MM:SS')
          const endDate = this.plans[planIndex].period_end 
            ? new Date(this.plans[planIndex].period_end)
            : new Date();
          
          this.$swal({
            title: 'Subscription Canceled',
            html: `
              <div class="text-left">
                <p class="mb-4">Your subscription has been scheduled for cancellation.</p>
                <div class="bg-gray-50 p-4 rounded-lg mb-4">
                  <p class="font-medium">Current subscription</p>
                  <p class="text-lg">${this.plans[planIndex].product?.name || 'REoptimizer'}</p>
                  <p class="text-gray-600">${this.formatCurrencyCents(this.plans[planIndex].price, this.plans[planIndex].currency)} per ${this.plans[planIndex].interval}</p>
                </div>
                <p class="text-sm text-gray-600">
                  Your subscription will be canceled, but is still available until the end of your billing period on
                  <strong>${endDate.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</strong>.
                </p>
                <p class="text-sm text-gray-500 italic mt-2">
                  If you change your mind, you can renew your subscription.
                </p>
              </div>
            `,
            icon: 'info',
            confirmButtonColor: '#3b82f6',
            confirmButtonText: 'Got it'
          });
        }
      } catch (error) {
        console.error('Error cancelling subscription:', error);
        this.$toast.error(error.response?.data?.message || 'Failed to cancel subscription. Please try again.');
      } finally {
        this.cancellingSubscription = null;
      }
    },
    
    confirmCancelSubscription(plan, event) {
      // Stop event propagation to prevent the parent click handler from triggering
      if (event) {
        event.stopPropagation();
        event.preventDefault();
      }
      
      // Parse the date string from the database (format: 'YYYY-MM-DD HH:MM:SS')
      const endDate = plan.period_end 
        ? new Date(plan.period_end)
        : new Date();
      
      this.$swal({
        title: 'Cancel Subscription?',
        html: `
          <div class="text-left">
            <p class="mb-4">Are you sure you want to cancel your subscription?</p>
            <div class="bg-gray-50 p-4 rounded-lg mb-4">
              <p class="font-medium">Current subscription</p>
              <p class="text-lg">${plan.product?.name || 'REoptimizer'}</p>
              <p class="text-gray-600">${this.formatCurrencyCents(plan.price, plan.currency)} per ${plan.interval}</p>
            </div>
            <p class="text-sm text-gray-600 mb-2">
              Your subscription will be canceled, but is still available until the end of your billing period on
              <strong>${endDate.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</strong>.
            </p>
            <p class="text-sm text-gray-500 italic">
              If you change your mind, you can renew your subscription.
            </p>
          </div>
        `,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#d33',
        cancelButtonColor: '#94a3b8',
        confirmButtonText: 'Yes, cancel subscription',
        cancelButtonText: 'Keep my subscription',
        reverseButtons: true
      }).then((result) => {
        if (result.value) {
          this.cancelSubscription(plan);
        }
      });
    },
    ...mapActions('billing', [
      'fetchCatalog',
      'setSelection',
      'updateAddonQuantity',
      'preview',
      'commitChanges'
    ]),

    mainplan() {
      // First check for a plan with has_subscription flag
      const subscribedPlan = this.catalog.find(c => c.category === 'plan' && c.has_subscription === true);
      if (subscribedPlan) return subscribedPlan;
      
      // Fallback to check if we have a current subscription in preview data
      if (this.previewData?.current_subscription?.price_id) {
        return this.catalog.find(c => c.id === this.previewData.current_subscription.price_id);
      }
      
      return null;
    },
    
    hasSubscription(catalog) {
      // Check both the catalog item's subscription status and if it's the current plan
      if (catalog?.has_subscription === true) return true;
      
      // Also check if this is the current plan in the preview data
      if (this.previewData?.current_subscription?.price_id === catalog?.id) return true;
      
      return false;
    },

    async selectPlan(catalog) {
      this.error = null;
      const planId = catalog.id;
      if (!planId) throw new Error('No plan ID provided');

      // Set the selected plan for the preview
      this.selectedPlan = catalog;
        
      // Set the selection with the plan details
      await this.setSelection({ 
          priceId: catalog.id,
          subscriptionId: this.mainplan()?.stripe_subscription_id || null,
          plan:catalog,
          category: catalog.category,
          quantity: catalog.quantity,
          update: catalog.category === 'plan' ? true : this.hasSubscription(catalog)
      });

      try {
        await this.confirmSelection();
      } catch (previewError) {
        console.error('Error updating preview:', previewError);
        throw previewError;
      }
    },
    
    formatCurrencyCents(amount, currency = 'USD') {
      return formatCurrencyCents(amount || 0, currency);
    },
    
    // Get block size for an addon
    getBlockSize(addon) {
      return addon.block_size ? parseInt(addon.block_size) : 1;
    },

    // Handle direct quantity update from input
    handleQuantityInput(event, addon) {
      const newQty = parseInt(event.target.value) || 0;
      const blockSize = this.getBlockSize(addon);
      
      // Round to nearest block size and ensure it's not below 0
      const roundedQty = Math.max(0, Math.round(newQty / blockSize) * blockSize);
      
      // Update the addonQuantities object
      this.$set(this.addonQuantities, addon.id, roundedQty);
    },
    
    // Handle update button click
    async updateAddonQuantity(priceId) {
      try {
        this.error = null;
        this.$set(this.loadingAddons, priceId, true);
        
        // Get the addon details
        const addon = this.addons.find(a => a.id === priceId);
        if (!addon) {
          throw new Error('Addon not found');
        }
        
        // Get the new quantity from addonQuantities or use the current quantity
        const newQty = this.addonQuantities[addon.id] || 0;
        
        if (newQty <= 0) {
          throw new Error('Quantity must be greater than 0');
        }

        // Set the selection with the addon details
        await this.setSelection({
          priceId: addon.id,
          subscriptionId: addon.stripe_subscription_id || null,
          plan: addon,
          category: addon.category || 'addon',
          quantity: parseInt(addon.quantity) + parseInt(newQty),
          update: addon.has_subscription
        });

        if (addon.has_subscription) {
          await this.confirmSelection();
        } 
        else {
          await this.handleCommit();
        }
        
      } catch (error) {
        console.error('Error updating addon quantity:', error);
        this.error = error.message || 'Failed to update addon quantity. Please try again.';
        this.$emit('error', this.error);
      } finally {
        // Reset loading state for this addon
        this.$set(this.loadingAddons, priceId, false);
      }
    },

    async confirmSelection() {
      console.log('Confirming selection for plan:', this.selection?.priceId);
      
      if (!this.selection?.priceId) {
        console.log('No plan selected, skipping preview');
        return false;
      }
      
      try {
        this.loading = true;
        console.log('Fetching preview for plan:', this.selection.priceId);
        const previewData = await this.preview();
        console.log('Preview Data:', previewData);
        
        if (previewData) {
          // Use $set to ensure reactivity
          previewData.selection = this.selection;
          this.$set(this, 'previewData', previewData);
          this.localPreview = previewData;
          this.showPreview = true; // Show preview after data is loaded
          return true;
        }
        return false;
      } catch (error) {
        console.error('Error fetching preview:', error);
        this.error = error.error || 'Failed to calculate changes. Please try again.';
        this.$emit('error', this.error);
        throw error; // Re-throw to be caught in selectPlan
      } finally {
        this.loading = false;
      }
    },

    async handleCommit() {
      // Prevent multiple clicks
      if (this.isUpdating) return;
      
      if (!this.selection?.priceId) {
        this.error = 'Please select a plan before continuing.';
        this.$emit('error', this.error);
        return;
      }
      
      try {
        this.isUpdating = true;
        this.error = null;
        this.localPreview = null;
        this.showPreview = false;
        
        // Commit changes and wait for completion
        const result = await this.commitChanges();
        
        // If we're redirecting to checkout, don't continue
        if (result?.redirecting) {
          return;
        }
        
        // Emit success - the store will handle the catalog refresh
        this.$emit('success', 'Subscription updated successfully');
        
        // Reset UI state
        this.selectedPlan = null;
        this.previewData = null;
      } catch (error) {
        console.error('Error committing changes:', error);
        this.error = error.message || 'Failed to update subscription. Please try again.';
        this.$emit('error', this.error);
        // Re-enable the button on error after a short delay
        setTimeout(() => {
          this.isUpdating = false;
        }, 1000);
      } finally {
        if (!this.error) {
          this.isUpdating = false;
        }
      }
    },

    /** Get feature name from feature object or string */
    getFeatureName(feature) {
      return typeof feature === 'string' ? feature : (feature?.name || feature?.id || '');
    },

    /** Get a unique key for feature items */
    getFeatureKey(feature) {
      if (typeof feature === 'string') return feature;
      return feature?.id || feature?.name || JSON.stringify(feature) || Math.random().toString(36).substr(2, 9);
    },

    /** Get the quantity of a specific addon including any pending changes */
    getAddonQuantity(priceId) {
      // Find the addon in the catalog
      const addon = this.addons.find(a => a.id === priceId);
      if (!addon) return 0;
      
      // Return the quantity from addonQuantities or the base quantity
      return this.addonQuantities[priceId] !== undefined 
        ? this.addonQuantities[priceId] 
        : (addon.quantity || 0);
    },
    
    /** Handle subscription-related errors */
    handleSubscriptionError(error) {
      console.error('Subscription error:', error);
      this.error = error.message || 'An error occurred with your subscription. Please try again.';
      this.$emit('error', this.error);
    },
  },
};
</script>
